/**
 * WizardAddKpiDialog — collect a KPI body to add to the WO Create Wizard's
 * pending list. Mirrors CreateKpiDialog's schema + UX but emits the body via
 * `onAdd(body)` instead of firing the create-KPI mutation directly. The
 * wizard creates the WO first, then iterates pendingKpis on Save.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatMachineName } from '@/lib/utils/machine-label';
import { KPI_UNITS, type CreateKpiBody, type KpiUnit } from '@/types/kpi';

interface WizardMachineOption {
  readonly machineId: number;
  readonly machineCode: string;
  readonly machineName: string;
  readonly vehicleNumber: string | null;
}

const wizardKpiSchema = z
  .object({
    name: z
      .string()
      .min(3, 'Name must be at least 3 characters')
      .max(100, 'Name must be at most 100 characters'),
    unit: z.enum([
      'Nos', 'Meters', 'RunningMeters', 'Hours', 'Kilometers',
      'Liters', 'Kilograms', 'Units', 'Trips', 'Custom',
    ] as const),
    customUnitText: z
      .string()
      .max(50, 'Custom unit must be at most 50 characters')
      .optional(),
    description: z
      .string()
      .max(500, 'Description must be at most 500 characters')
      .optional(),
    targetPerShift: z.preprocess(
      (v) => (v === '' || v === undefined || v === null ? undefined : Number(v)),
      z.number().positive('Target must be positive').optional(),
    ),
    applicableMachineIds: z
      .array(z.number())
      .min(1, 'Select at least one machine')
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.unit === 'Custom') {
      if (
        data.customUnitText === undefined ||
        data.customUnitText.trim() === ''
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customUnitText'],
          message: 'Custom unit text is required when unit is Custom',
        });
      }
    } else if (
      data.customUnitText !== undefined &&
      data.customUnitText.trim() !== ''
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customUnitText'],
        message: 'Custom unit text is only allowed when unit is Custom',
      });
    }
  });

type WizardKpiFormValues = z.infer<typeof wizardKpiSchema>;

export interface WizardAddKpiDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
  readonly machines: readonly WizardMachineOption[];
  readonly onAdd: (body: CreateKpiBody) => void;
  /**
   * Optional pre-populated values (template-copy path). Read on mount only —
   * the parent should conditionally mount this dialog so each open reads fresh
   * defaults via react-hook-form.
   */
  readonly initialValues?: Partial<CreateKpiBody> | undefined;
}

export function WizardAddKpiDialog({
  open,
  onOpenChange,
  machines,
  onAdd,
  initialValues,
}: WizardAddKpiDialogProps): JSX.Element {
  const defaultMachineIds = machines.map((m) => m.machineId);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<WizardKpiFormValues>({
    resolver: zodResolver(wizardKpiSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      unit: initialValues?.unit ?? 'Nos',
      customUnitText: initialValues?.customUnitText ?? '',
      description: initialValues?.description ?? '',
      targetPerShift: initialValues?.targetPerShift,
      applicableMachineIds: initialValues?.applicableMachineIds
        ? [...initialValues.applicableMachineIds]
        : defaultMachineIds,
    },
  });

  const watchedUnit = watch('unit');
  const watchedMachineIds = watch('applicableMachineIds') ?? defaultMachineIds;

  function handleClose(): void {
    reset();
    onOpenChange(false);
  }

  function onSubmit(values: WizardKpiFormValues): void {
    const body: CreateKpiBody = {
      name: values.name,
      unit: values.unit,
      ...(values.unit === 'Custom' && values.customUnitText
        ? { customUnitText: values.customUnitText }
        : {}),
      ...(values.description && values.description.trim() !== ''
        ? { description: values.description }
        : {}),
      ...(values.targetPerShift !== undefined
        ? { targetPerShift: values.targetPerShift }
        : {}),
      ...(values.applicableMachineIds !== undefined &&
      values.applicableMachineIds.length > 0
        ? { applicableMachineIds: values.applicableMachineIds }
        : {}),
    };
    onAdd(body);
    handleClose();
  }

  function toggleMachine(machineId: number, checked: boolean): void {
    const current = watchedMachineIds;
    const next = checked
      ? [...current, machineId]
      : current.filter((id) => id !== machineId);
    setValue('applicableMachineIds', next);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="wizard-add-kpi-dialog">
        <DialogHeader>
          <DialogTitle>Add KPI</DialogTitle>
          <DialogDescription>
            Define a KPI to attach to this Work Order on save. You can add more
            after creating the WO from its detail page.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="wkpi-name">Name</Label>
            <Input
              id="wkpi-name"
              data-testid="wkpi-name-input"
              {...register('name')}
              placeholder="e.g. Throughput"
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="wkpi-unit">Unit</Label>
            <Select
              value={watchedUnit}
              onValueChange={(v) => { setValue('unit', v as KpiUnit); }}
            >
              <SelectTrigger id="wkpi-unit" data-testid="wkpi-unit-trigger">
                <SelectValue placeholder="Select unit…" />
              </SelectTrigger>
              <SelectContent>
                {KPI_UNITS.map((u) => (
                  <SelectItem key={u} value={u}>
                    {u === 'RunningMeters' ? 'Running Meters' : u}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.unit && (
              <p className="text-sm text-destructive">{errors.unit.message}</p>
            )}
          </div>

          {watchedUnit === 'Custom' && (
            <div className="space-y-1">
              <Label htmlFor="wkpi-custom-unit">Custom Unit Text</Label>
              <Input
                id="wkpi-custom-unit"
                data-testid="wkpi-custom-unit-input"
                {...register('customUnitText')}
                placeholder="e.g. Pallets"
              />
              {errors.customUnitText && (
                <p className="text-sm text-destructive">
                  {errors.customUnitText.message}
                </p>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="wkpi-description">Description (optional)</Label>
            <Textarea
              id="wkpi-description"
              {...register('description')}
              placeholder="What this KPI measures…"
              rows={2}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="wkpi-target">Target per Shift (optional)</Label>
            <Input
              id="wkpi-target"
              data-testid="wkpi-target-input"
              type="number"
              step="any"
              min="0"
              {...register('targetPerShift')}
              placeholder="e.g. 100"
            />
            {errors.targetPerShift && (
              <p className="text-sm text-destructive">
                {errors.targetPerShift.message}
              </p>
            )}
          </div>

          {machines.length > 0 && (
            <div className="space-y-2">
              <Label>Applicable Machines</Label>
              <div className="space-y-1">
                {machines.map((m) => (
                  <div key={m.machineId} className="flex items-center gap-2">
                    <Checkbox
                      id={`wkpi-machine-${String(m.machineId)}`}
                      data-testid={`wkpi-machine-checkbox-${String(m.machineId)}`}
                      checked={watchedMachineIds.includes(m.machineId)}
                      onCheckedChange={(checked) => {
                        toggleMachine(m.machineId, checked === true);
                      }}
                    />
                    <Label
                      htmlFor={`wkpi-machine-${String(m.machineId)}`}
                      className="font-normal cursor-pointer"
                    >
                      {formatMachineName(m.machineName, m.vehicleNumber, m.machineCode)}
                    </Label>
                  </div>
                ))}
              </div>
              {errors.applicableMachineIds && (
                <p className="text-sm text-destructive">
                  {errors.applicableMachineIds.message}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              data-testid="wkpi-cancel"
            >
              Cancel
            </Button>
            <Button type="submit" data-testid="wkpi-add">
              Add KPI
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
